import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { FaCaretUp, FaCaretDown, FaClipboardList } from 'react-icons/fa';
import FormattedListValue from '@/shared/components/FormattedListValue';
import { FormattedMessage } from 'react-intl';
import { IconPencil, DeleteIcon, CopyIcon } from '@/shared/components/Icons';
import { CanCreate, CanUpdate, CanDelete } from '@/shared/components/security';
import messages from './invoiceScheduleMessages';
import InvoiceScheduleLinesTable from './InvoiceScheduleLinesTable';
import intlShape from '@/shared/utils/intlShape';
import Checkbox from '@/shared/components/input/Checkbox';
import ZendeskHelp from '@/shared/components/ZendeskHelp';
import AuthService, { features } from '@/shared/services/AuthService';

const InvoiceSchedulesTable = ({
  schedules,
  onToggleDetails,
  onEditSchedule,
  onCopySchedule,
  onDeleteSchedule,
  invoiceTexts,
  onTranslate,
  taskTemplates,
  intl,
}) => {
  const hasTaskflowBilling = schedules && schedules.some(x => x.isTaskflowBilling);
  const hasReferenceYear = AuthService.hasFeature(features.ReferenceYear);
  return (
    <table className="table table-grid table-hover">
      <thead>
        <tr>
          <th />
          <th>
            <FormattedMessage {...messages.Name} />
          </th>
          <th>
            <FormattedMessage {...messages.InvoiceScheduleType} />
          </th>
          {hasTaskflowBilling ? (
            <th className="text-center">
              <FormattedMessage {...messages.HasTaskflowBilling} />
              <ZendeskHelp article="19193674748188" />
            </th>
          ) : null}
          {hasReferenceYear ? (
            <th className="text-center">
              <FormattedMessage {...messages.BasedOnReferenceYear} />
              <ZendeskHelp article="23291843922716" />
            </th>
          ) : null}
          <th />
        </tr>
      </thead>
      {schedules &&
        schedules.map(schedule => (
          <tbody className={classnames({ 'highlight-pulse': !!schedule.isNew })} key={schedule.id}>
            <tr onClick={() => onToggleDetails(schedule.id)}>
              <td>{schedule.showDetails ? <FaCaretUp /> : <FaCaretDown />}</td>
              <td>
                <CanUpdate
                  on="Settings"
                  fallback={
                    <label>
                      <FormattedListValue
                        list="invoiceschedule"
                        id={schedule.id}
                        fallback={schedule.name}
                      />
                    </label>
                  }
                >
                  <label
                    className="bold clickable"
                    onClick={e => {
                      e.stopPropagation();
                      onTranslate(schedule);
                    }}
                  >
                    <FormattedListValue
                      list="invoiceschedule"
                      id={schedule.id}
                      fallback={schedule.name}
                    />
                  </label>
                </CanUpdate>
              </td>
              <td>
                <label>
                  <FormattedListValue list="invoicingtype" id={schedule.invoicingType} />
                </label>
              </td>

              {hasTaskflowBilling ? (
                <td className="text-center">
                  {schedule.isTaskflowBilling && <i className="fa fa-fw fa-check-square-o" />}
                </td>
              ) : null}
              {hasReferenceYear ? (
                <td className="text-center">
                  {schedule.useReferenceYear && <i className="fa fa-fw fa-check-square-o" />}
                </td>
              ) : null}
              <td className="text-right">
                <div className="flex-row-nowrap">
                  <CanUpdate on="Settings">
                    <a
                      className="btn btn-xs btn-icon"
                      onClick={e => {
                        onEditSchedule(schedule);
                        e.stopPropagation();
                      }}
                    >
                      <IconPencil />
                    </a>
                  </CanUpdate>
                  <CanCreate on="Settings">
                    <a
                      className="btn btn-xs btn-icon"
                      onClick={e => {
                        onCopySchedule(schedule);
                        e.stopPropagation();
                      }}
                    >
                      <CopyIcon />
                    </a>
                  </CanCreate>
                  <CanDelete on="Settings">
                    <a
                      className="btn btn-xs btn-icon"
                      onClick={e => {
                        onDeleteSchedule(schedule);
                        e.stopPropagation();
                      }}
                    >
                      <DeleteIcon />
                    </a>
                  </CanDelete>
                </div>
              </td>
            </tr>
            {schedule.showDetails && (
              <tr className="subclasses">
                <td />
                <td colSpan="4">
                  <InvoiceScheduleLinesTable
                    lines={schedule.lines}
                    schedule={schedule}
                    invoiceTexts={invoiceTexts}
                    taskTemplates={taskTemplates}
                  />
                </td>
              </tr>
            )}
          </tbody>
        ))}
    </table>
  );
};

InvoiceSchedulesTable.propTypes = {
  schedules: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      invoicingType: PropTypes.number,
    }),
  ).isRequired,
  onToggleDetails: PropTypes.func.isRequired,
  onEditSchedule: PropTypes.func.isRequired,
  onCopySchedule: PropTypes.func.isRequired,
  onDeleteSchedule: PropTypes.func.isRequired,
  onTranslate: PropTypes.func.isRequired,
  invoiceTexts: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  taskTemplates: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  intl: intlShape.isRequired,
};

export default InvoiceSchedulesTable;
